/**
 * @ Author: Housefolios
 * @ Create Time: 2021-09-16 13:21:50
 * @ Modified by: David Helmick
 * @ Modified time: 2025-10-24 18:07:58
 * @ Description: Dialog for sharing a property with another through email
 */

import React, { useState } from 'react'
import ShareIcon from '@mui/icons-material/Share'
import {
  Grid,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemButton,
  MenuItem,
  TextField,
  Typography,
  Box,
} from '@mui/material'

// Centralized lodash imports - much cleaner!
import { map, head, isEmpty, get, find } from '@/utils/lodash'

import { enqueueSnackbar } from 'notistack'
import withSession from '@/housefolios-components/Session/withSession'
import {
  TextValidator,
  ValidatorForm,
} from '@/housefolios-components/shared/muiFormValidator'
import { GET_ORGS_PROPERTY_SHARED_WITH } from '../queries'
import { useMutation, useQuery } from '@apollo/client/react'
import { SHARE_PROPERTY } from '../mutations'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import RemoveSharedProperties from '@/housefolios-components/Properties/RemoveSharedProperties'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import UpdateSharedProperties from '@/housefolios-components/Properties/UpdateSharedProperties'
import { useShareEmails } from '@/hooks/useShareEmails'
import ShareEmailsAutocomplete from '@/housefolios-components/FormComponents/ShareEmailsAutocomplete'

function SharePropertyComponent(props) {
  const { notifications, rightClickMenu, propertyId } = props

  const shareEmails = useShareEmails()
  const [sharePermission, setSharePermission] = useState('viewOnly')
  const [openShareProperty, setOpenShareProperty] = useState(false)
  const [loader, setLoader] = useState(false)
  const handleOpenShareProperty = () => {
    setOpenShareProperty(true)
    shareEmails.resetEmails()
  }
  const handleCloseShareProperty = () => {
    setOpenShareProperty(false)
    setLoader(false)
  }

  const [shareProperty] = useMutation(SHARE_PROPERTY)

  // Only fetch when the dialog is open with a real propertyId.
  // Bulk actions mount this with null until a selection exists; ID! rejects null → 400.
  const { data } = useQuery(GET_ORGS_PROPERTY_SHARED_WITH, {
    variables: { propertyId },
    skip: !openShareProperty || !propertyId,
  })
  const orgsPropertySharedWith = data?.orgsPropertySharedWith ?? []

  return (
    <>
      {notifications && (
        <MenuItem
          onClick={handleOpenShareProperty}
          sx={{ my: '0.25rem' }}
        >
          <FontAwesomeIcon icon={['fas', 'share-nodes']} style={{ marginRight: 10 }} sx={{ fontSize: "1.15rem !important" }} />
          <span>Share Property</span>
        </MenuItem>
      )}
      {rightClickMenu && (
        <ListItemButton onClick={handleOpenShareProperty}>
          <Grid container direction="row" spacing={1}>
            <Grid data-hf-grid="keep">
              {/* <ShareIcon /> */}
              <FontAwesomeIcon icon={['fas', 'share-nodes']} style={{ marginRight: 2 }} sx={{ fontSize: "1.15rem !important" }} />
            </Grid>
            <Grid data-hf-grid="keep">
              <Typography>Share Property</Typography>
            </Grid>
          </Grid>
        </ListItemButton>
      )}
      {openShareProperty && (
        <Dialog
          open={openShareProperty}
          onClose={handleCloseShareProperty}
          maxWidth="sm"
          fullWidth={true}
          scroll="body"
          onKeyDown={(event) => event.stopPropagation()}
        >
          <DialogTitle>Share this property</DialogTitle>
          <BlockUi
            blocking={loader}
            loader={
              <PacmanLoader color={'var(--primary)'} loading={true} />
            }
            message={
              <Box sx={{ color: '#368cff' }}>Sharing Property</Box>
            }
          >
            <ValidatorForm
              onSubmit={(event) => {
                event.preventDefault()
                const finalEmails = shareEmails.collectEmails()
                if (isEmpty(finalEmails)) {
                  enqueueSnackbar(
                    'Enter at least one valid email address',
                    {
                      variant: 'warning',
                      autoHideDuration: 4000,
                    },
                  )
                  return
                }
                setLoader(true)
                shareProperty({
                  variables: {
                    id: propertyId,
                    emails: finalEmails,
                    sharePermission,
                  },
                })
                  .then(() => {
                    enqueueSnackbar(`Email Sent`, {
                      variant: 'success',
                      autoHideDuration: 3000,
                    })
                    handleCloseShareProperty()
                  })
                  .catch((error) => {
                    enqueueSnackbar(`Failed to send email`, {
                      variant: 'error',
                      autoHideDuration: 3000,
                    })
                    setLoader(false)
                  })
              }}
              style={{
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <DialogContent>
                <Grid container direction="row">
                  <Grid size={12}>
                    <ShareEmailsAutocomplete
                      shareEmails={shareEmails}
                    />
                    {/* <TextValidator
                                           name="email"
                                           type="email"
                                           variant='outlined'
                                           margin='dense'
                                           autoFocus
                                           onChange={event => setEmail(event.target.value)}
                                           value={email}
                                           placeholder="Enter email address..."
                                           label="Email"
                                           fullWidth={true}
                                           validators={[
                                           'required',
                                           'maxStringLength: 50',
                                           'isEmail',
                                           ]}
                                           errorMessages={[
                                           'This field is required',
                                           'You cannot exceed 50 characters',
                                           'Not a valid email address',
                                           ]}
                                       /> */}
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      select
                      name="sharePermission"
                      variant="outlined"
                      margin="dense"
                      label="Permission"
                      fullWidth={true}
                      // style={{ width: 175 }}
                      value={sharePermission}
                      onChange={(event) =>
                        setSharePermission(event.target.value)
                      }
                      required={true}
                    >
                      <MenuItem key={'viewOnly'} value={'viewOnly'}>
                        {'View Only'}
                      </MenuItem>
                      <MenuItem key={'canEdit'} value={'canEdit'}>
                        {'Can Edit'}
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={12} style={{ padding: 10 }}>
                    {!isEmpty(orgsPropertySharedWith) && (
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <Typography>Shared With</Typography>
                        </Grid>
                        {map(orgsPropertySharedWith, (org) => (
                          <Grid size={12} key={org._id || org.email || org.name}>
                            <Grid
                              container
                              direction="row"
                              spacing={2}
                              sx={{
                                alignItems: 'center',
                              }}
                            >
                              <Grid>
                                <Avatar>{head(org.name)}</Avatar>
                              </Grid>
                              <Grid>
                                <Grid container direction="column" sx={{ lineHeight: "1.3" }}>
                                  <Grid>
                                    <Box component="b" sx={{ fontSize: "1.15rem !important" }}>
                                      {org.name}
                                    </Box>
                                  </Grid>
                                  <Grid>
                                    <Box
                                      component="span"
                                      sx={{
                                        color: 'rgba(7, 9, 25, 0.5)',
                                        display: 'block',
                                      }}
                                    >
                                      {org.email}
                                    </Box>
                                  </Grid>
                                  <Grid>
                                    <Box
                                      component="span"
                                      sx={{
                                        color: 'rgba(7, 9, 25, 0.5)',
                                        display: 'block',
                                      }}
                                    >
                                      {get(
                                        find(org.sharedProperties, [
                                          'id',
                                          propertyId,
                                        ]),
                                        'sharePermission',
                                      ) === 'canEdit'
                                        ? 'Can Edit'
                                        : 'View Only'}
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Typography style={{ flex: 1 }} />
                              <Grid>
                                <UpdateSharedProperties
                                  orgId={org._id}
                                  selected={[propertyId]}
                                  shareAccess
                                  sharePermission={get(
                                    find(org.sharedProperties, [
                                      'id',
                                      propertyId,
                                    ]),
                                    'sharePermission',
                                  )}
                                />
                              </Grid>
                              <Grid>
                                <RemoveSharedProperties
                                  orgId={org._id}
                                  selected={[propertyId]}
                                  shareAccess
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleCloseShareProperty}
                >
                  <Typography>CANCEL</Typography>
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  type="submit"
                  disabled={loader}
                >
                  <Typography>SHARE</Typography>
                </Button>
              </DialogActions>
            </ValidatorForm>
          </BlockUi>
        </Dialog>
      )}
    </>
  )
}
export default withSession(SharePropertyComponent)
